import React, { useEffect, useState, useContext } from "react";
import uuid from "uuid";
import _ from "lodash";
import { makeStyles } from "@material-ui/core/styles";
import { Card, Typography } from "@material-ui/core";
import { RouteComponentProps } from "react-router";
import { connect } from "react-redux";

import { PageHeader, PageRoot, Suggestion, Button } from "../../components";
import { UIMessageContext } from "../../components/UIMessage";

import routes from "../../constants/routes";
import {
  Premises,
  LegalEntity,
  ReportType,
  ReportTypeLabels
} from "../../constants/interfaces";

import { premisesActions } from "../../reducers/premises";

import {
  GENERAL_FILE_TYPES,
  GENERAL_ONE_FILE_HINT,
  MAX_FILE_SIZE
} from "../../constants/files";
import { INFORMATION_ACCURACY_CONFIRMATION } from "../../constants/messages";
import { RxForm, FormValues, FieldValueOnChange } from "../../components/Form";
import SelectField from "../../components/Fields/SelectField";

import FileUploadField from "../../components/Fields/FileUploadField";
import DatePickerField from "../../components/Fields/DatePickerField";
import { OnUnSaveChange } from "../../components/Form/OnUnSaveChange";
import { SubmitButton } from "../../components/Form/SubmitButton";
import { ResetButton } from "../../components/Form/ResetButton";
import api from "../../utils/api";

const useStyles = makeStyles(theme => ({
  buttonWrapper: {
    display: "flex",
    marginTop: theme.spacing(2)
  },
  card: {
    overflow: "visible",
    padding: theme.spacing(4)
  },
  select: {
    margin: theme.spacing(2),
    minWidth: "300px"
  },
  title: {
    marginBottom: theme.spacing(3)
  },
  dropzone: {
    marginBottom: theme.spacing(4),
    marginTop: theme.spacing(1)
  },
  row: {
    display: "flex"
  },
  monthHeader: {
    display: "flex",
    justifyContent: "center",
    backgroundColor: theme.palette.primary.main,
    paddingLeft: 24,
    paddingRight: 24,
    fontSize: "1.6rem",
    fontFamily: `'Noto Sans',Arial,sans-serif`,
    color: "#fff"
  },
  "@media (max-width: 820px)": {
    row: {
      flexDirection: "column"
    }
  }
}));

const MONTH_MAPPER = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sept",
  "Oct",
  "Nov",
  "Dec"
];

type Props = RouteComponentProps & {
  getPremises: () => Promise<void>;
  premises: Premises[];
};

const ControlledDrugsReporting: React.FC<Props> = props => {
  const classes = useStyles();
  const [tempCaseId, resetTempCaseId] = useState(uuid());
  const { showMessage } = useContext(UIMessageContext);
  useEffect(() => {
    props.getPremises();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PageRoot>
      <PageHeader
        breadcrumbLinks={[
          {
            label: routes.HOME.label,
            route: routes.HOME.url
          }
        ]}
        label={routes.CONTROLLED_DRUGS.label}
      />
      <Card className={classes.card}>
        <Typography className={classes.title} color="primary" variant="h5">
          Submit controlled drugs report
        </Typography>

        <RxForm
          confirmMessage={INFORMATION_ACCURACY_CONFIRMATION}
          onSubmit={async (value, meta, { resetForm }) => {
            const report = {
              tempCaseId,
              legalEntityId: !value.legalEntity
                ? undefined
                : value.legalEntity.cmAcmCaseIdentifier,
              premisesId: !value.premises
                ? undefined
                : value.premises.cmAcmCaseIdentifier,
              premisesName: !value.premises
                ? undefined
                : value.premises.premisesName,
              reportType: value.reportType,
              reportYear: !value.reportYear
                ? undefined
                : value.reportYear.getFullYear(),
              reportMonth: !value.reportMonth
                ? undefined
                : MONTH_MAPPER[value.reportMonth.getMonth()]
            };

            try {
              await api.put(
                `/cd/raiseControlledDrugsReport/${tempCaseId}`,
                report
              );
              resetForm();
              resetTempCaseId(uuid());
              showMessage("Your report was successfully submitted", "success");
            } catch (error) {
              showMessage(
                _.get(error, "response.data.message") || error.message,
                "error"
              );
            }
          }}
        >
          <OnUnSaveChange />
          <FieldValueOnChange>
            {({ action, formState, updateFormValues, updateFormState }) => {
              if (
                action.name === "legalEntity" &&
                action.type === "@@FORM_CHANGE"
              ) {
                const nextFormState = {
                  values: {
                    ...formState.values,
                    premises: null
                  },
                  meta: {
                    ...formState.meta,
                    completed: false
                  },
                  fields: {
                    ...formState.fields,
                    premises: {
                      ...formState.fields.premises,
                      completed: false
                    }
                  }
                };

                updateFormState(nextFormState);
              } else if (
                action.name === "premises" &&
                action.type === "@@FORM_CHANGE"
              ) {
                const formLegalEntity = formState.values.legalEntity;
                if (formLegalEntity) return null;
                const propsLegalEntity = props.premises.find(
                  p =>
                    p.cmAcmCaseIdentifier === action.payload.cmAcmCaseIdentifier
                );
                const nextFormValues = {
                  ...formState.values,
                  legalEntity: (propsLegalEntity as Premises).parentLegalEntity
                };
                updateFormValues(nextFormValues);
              } else if (
                action.name === "reportType" &&
                action.type === "@@FORM_CHANGE"
              ) {
                if (formState.values.reportType === "CDAnnual" || formState.values.reportType === "HempAnnual") {
                  updateFormValues({
                    ...formState.values,
                    reportMonth: null
                  });
                }
              }

              return null;
            }}
          </FieldValueOnChange>

          <FormValues>
            {({ formValues }) => {
              const { legalEntity, reportType } = formValues;

              const legalEntitiesSelectList = props.premises.reduce(
                (result: Suggestion[], premise) => {
                  const le = premise.parentLegalEntity;
                  if (!le || !le.viewable) return result;
                  if (
                    result.some(
                      r =>
                        r.value.cmAcmCaseIdentifier === le.cmAcmCaseIdentifier
                    )
                  )
                    return result;
                  result.push({
                    label: le.legalEntityName,
                    value: le
                  });
                  return result;
                },
                []
              );

              const premisesSelectList = props.premises
                .filter(premise => {
                  if (!premise.parentLegalEntity) return false;
                  if (!legalEntity) return true;
                  return (
                    premise.parentLegalEntity.cmAcmCaseIdentifier ===
                      (legalEntity as LegalEntity).cmAcmCaseIdentifier
                  );
                })
                .map(premise => ({
                  label: premise.premisesName,
                  value: premise
                }));

              return (
                <>
                  <div className={classes.row}>
                    <SelectField
                      id="legal-entity"
                      label="Legal Entity"
                      name="legalEntity"
                      getSelectedValue={selected =>
                        selected ? selected.value : null
                      }
                      className={classes.select}
                      placeholder="Select legal entity"
                      getValueDisplay={value =>
                        value ? value.legalEntityName : ""
                      }
                      suggestions={legalEntitiesSelectList}
                    />
                    <SelectField
                      id="premises"
                      label="Premises *"
                      name="premises"
                      required
                      getSelectedValue={selected =>
                        selected ? selected.value : null
                      }
                      className={classes.select}
                      placeholder="Select premises"
                      getValueDisplay={value =>
                        value ? value.premisesName : ""
                      }
                      suggestions={premisesSelectList}
                    />
                  </div>
                  <SelectField
                    id="report-type"
                    required
                    label="Report type"
                    name="reportType"
                    getSelectedValue={selected =>
                      selected ? selected.value : null
                    }
                    getValueDisplay={type => ReportTypeLabels.get(type) || ""}
                    className={classes.select}
                    placeholder=""
                    suggestions={Object.keys(ReportType).map(type => ({
                      label: ReportTypeLabels.get(type) || type,
                      value: type
                    }))}
                  />
                  <div className={classes.row}>
                    <DatePickerField
                      name="reportYear"
                      required
                      className={classes.select}
                      format="yyyy"
                      label="Report year"
                      placeholder="Year"
                      minDate={new Date("2016-01-01")}
                      maxDate={new Date("2040-12-31")}
                      views={["year"]}
                    />
                    <DatePickerField
                      name="reportMonth"
                      required={reportType === "CDMonthly"}
                      disabled={reportType === "CDAnnual" || reportType === "HempAnnual"}
                      className={classes.select}
                      format="MMMM"
                      ToolbarComponent={toolbarProps => {
                        return (
                          <div className={classes.monthHeader}>
                            <span>
                              <h3>
                                {MONTH_MAPPER[toolbarProps.date.getMonth()]}
                              </h3>
                            </span>
                          </div>
                        );
                      }}
                      label="Report month"
                      placeholder="Month"
                      views={["month"]}
                    />
                  </div>
                </>
              );
            }}
          </FormValues>

          <div style={{ margin: "1em" }}>
            <Typography color="textSecondary" variant="caption">
              Attach report
            </Typography>
            <FileUploadField
              required
              handleError={(error: Error) => {
                showMessage(
                  _.get(error, "response.data.message") || error.message,
                  "error"
                );
              }}
              onFileUpload={(files: File[]) =>
                Promise.all(
                  files.map(file => {
                    const formData: any = new FormData();
                    formData.append("uploadfile", file);
                    return api.post(`/cd/uploadFile/${tempCaseId}`, formData);
                  })
                )
              }
              onFileRemove={(file: File) =>
                api.put(`/cd/removeFile/${tempCaseId}`, file.name, {
                  headers: {
                    "Content-Type": "application/json"
                  }
                })
              }
              acceptedFileTypes={GENERAL_FILE_TYPES}
              className={classes.dropzone}
              hint={GENERAL_ONE_FILE_HINT}
              name="attachments"
              id="attachments"
              maxFileCount={1}
              maxTotalByteSize={MAX_FILE_SIZE}
            />
          </div>
          <div style={{ margin: "1em" }}>
            <SubmitButton label="Submit" />
            <ResetButton
              label="Reset"
              onReset={() => {
                api.put(`/ri/removeFiles/${tempCaseId}`);
              }}
            />
            <Button
              onClick={() => {
                props.history.push(routes.HOME.url);
              }}
              id="close"
            >
              Close
            </Button>
          </div>
        </RxForm>
      </Card>
    </PageRoot>
  );
};

const mapDispatchToProps = {
  getPremises: premisesActions.getPremises
};

const mapStateToProps = ({ premises }: any) => ({
  premises: premises.premises
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ControlledDrugsReporting);
